// services
import AxiosInstanceCreator from 'services/api';

// models
import type {
  ReqRiskDatabaseActivity,
  ReqRiskDatabaseHazard,
  ReqRiskDatabaseWorks,
  ResRiskDatabaseActivity,
  ResRiskDatabaseHazard,
  ResRiskDatabaseWorks,
  RiskDatabaseActivityBatchItem,
  RiskDatabaseHazardBatchItem,
  RiskDatabaseWorksBatchItem
} from '../models';

const riskAssessmentRiskDatabaseInstance = new AxiosInstanceCreator({
  baseURL: `/v1/api/risk-assessment/setting/risk-database`
}).create();

export const riskDatabaseApi = {
  /**
   * 위험 평가 설정 위험 데이터베이스 작업 조회
   *
   * @param {string} disciplineId
   * @returns
   */
  async getRiskDatabaseWorks(disciplineId: string): Promise<ResRiskDatabaseWorks> {
    return await riskAssessmentRiskDatabaseInstance
      .get(`/works?disciplineId=${disciplineId}`)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 작업 생성
   *
   * @param {ReqRiskDatabaseWorks} data
   * @returns
   */
  async addRiskDatabaseWorks(data: ReqRiskDatabaseWorks) {
    return await riskAssessmentRiskDatabaseInstance.post('/works', data).then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 작업 일괄 생성
   *
   * @param {RiskDatabaseWorksBatchItem} data
   * @returns
   */
  async addRiskDatabaseWorksBatch(data: RiskDatabaseWorksBatchItem) {
    return await riskAssessmentRiskDatabaseInstance
      .post('/works/batch', data)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 작업 삭제
   *
   * @param {number} worksId
   * @returns
   */
  async deleteRiskDatabaseWorks(worksId: number) {
    return await riskAssessmentRiskDatabaseInstance
      .delete(`/works/${worksId}`)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 작업 수정
   *
   * @param {number} worksId
   * @param {ReqRiskDatabaseWorks} data
   *
   * @returns
   */
  async updateRiskDatabaseWorks(worksId: number, data: ReqRiskDatabaseWorks) {
    return await riskAssessmentRiskDatabaseInstance
      .put(`/works/${worksId}`, data)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 작업 엑셀 업로드
   *
   * @param {FormData} data
   *
   * @returns
   */
  async excelAddRiskDatabaseWorks(data: FormData) {
    return await riskAssessmentRiskDatabaseInstance
      .post('/works/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 활동 조회
   *
   * @param {number} worksId
   *
   * @returns
   */
  async getRiskDatabaseActivity(worksId: number): Promise<ResRiskDatabaseActivity> {
    return await riskAssessmentRiskDatabaseInstance
      .get(`/activity?worksId=${worksId}`)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 활동 생성
   *
   * @param {ReqRiskDatabaseActivity} data
   *
   * @returns
   */
  async addRiskDatabaseActivity(data: ReqRiskDatabaseActivity) {
    return await riskAssessmentRiskDatabaseInstance.post('/activity', data).then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 활동 일괄 생성
   *
   * @param {RiskDatabaseActivityBatchItem} data
   *
   * @returns
   */
  async addRiskDatabaseActivityBatch(data: RiskDatabaseActivityBatchItem) {
    return await riskAssessmentRiskDatabaseInstance
      .post('/activity/batch', data)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 활동 삭제
   *
   * @param {number} activityId
   *
   * @returns
   */
  async deleteRiskDatabaseActivity(activityId: number) {
    return await riskAssessmentRiskDatabaseInstance
      .delete(`/activity/${activityId}`)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 활동 수정
   *
   * @param {number} activityId
   * @param {ReqRiskDatabaseActivity} data
   *
   * @returns
   */
  async updateRiskDatabaseActivity(activityId: number, data: ReqRiskDatabaseActivity) {
    return await riskAssessmentRiskDatabaseInstance
      .put(`/activity/${activityId}`, data)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 위험 조회
   *
   * @param {number} activityId
   *
   * @returns
   */
  async getRiskDatabaseHazard(activityId: number): Promise<ResRiskDatabaseHazard> {
    return await riskAssessmentRiskDatabaseInstance
      .get(`/hazard?activityId=${activityId}`)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 위험 생성
   *
   * @param {ReqRiskDatabaseHazard} data
   *
   * @returns
   */
  async addRiskDatabaseHazard(data: ReqRiskDatabaseHazard) {
    return await riskAssessmentRiskDatabaseInstance.post('/hazard', data).then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 위험 일괄 생성
   *
   * @param {RiskDatabaseHazardBatchItem} data
   *
   * @returns
   */
  async addRiskDatabaseHazardBatch(data: RiskDatabaseHazardBatchItem) {
    return await riskAssessmentRiskDatabaseInstance
      .post('/hazard/batch', data)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 위험 삭제
   *
   * @param {number} hazardId
   *
   * @returns
   */
  async deleteRiskDatabaseHazard(hazardId: number) {
    return await riskAssessmentRiskDatabaseInstance
      .delete(`/hazard/${hazardId}`)
      .then((res) => res.data);
  },

  /**
   * 위험 평가 설정 위험 데이터베이스 위험 수정
   *
   * @param {number} hazardId
   * @param {ReqRiskDatabaseHazard} data
   *
   * @returns
   */
  async updateRiskDatabaseHazard(hazardId: number, data: ReqRiskDatabaseHazard) {
    return await riskAssessmentRiskDatabaseInstance
      .put(`/hazard/${hazardId}`, data)
      .then((res) => res.data);
  }
};
